import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  Landmark, Users, DollarSign, FileText, Percent, Link2, Plug, ArrowLeftRight, Wallet, ShoppingCart, CreditCard,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { useModuleLicense } from '../../hooks/useModuleLicense';

/**
 * /accounts — the money side of the organisation, in one place.
 *
 * The nursery grows, picks and despatches; payroll approves a pay run and
 * sends it. Who is billed, at what price, what they owe, and which Xero or
 * MYOB the organisation posts to are set up here, by someone holding an
 * Accounts seat. Any seat reads; the Accounts Admin seat moves money and
 * changes a connection.
 */

type Tile = { label: string; description: string; icon: LucideIcon; path: string };

const SALES: Tile[] = [
  { label: 'Customers', icon: Users, path: '/accounts/customers',
    description: 'Head offices and their sites, payment terms, credit limits and who pays' },
  { label: 'Price lists', icon: DollarSign, path: '/accounts/price-lists',
    description: 'Base, tier and contract prices, with volume breaks' },
  { label: 'Invoices', icon: FileText, path: '/accounts/invoices',
    description: 'Every invoice and what each still owes' },
  { label: 'Receivables', icon: Wallet, path: '/accounts/receivables',
    description: 'Who owes what, payments, short payments and credit notes' },
  { label: 'Rebates', icon: Percent, path: '/accounts/rebates',
    description: 'What each paying customer gets back on its spend' },
];

const PURCHASING: Tile[] = [
  { label: 'Purchase orders', icon: ShoppingCart, path: '/purchase-orders',
    description: 'What has been ordered, for which farm, from whom and by whom' },
];

// One connection to the organisation's own Xero serves invoices and payroll.
const XERO: Tile = { label: 'Xero', icon: Link2, path: '/accounts/xero', description: '' };
const xeroTile = (sells: boolean, paysStaff: boolean): Tile => ({
  ...XERO,
  description: sells && paysStaff
    ? 'Connect your Xero once: invoices and credit notes go to it, payments come back, and payroll is sent to it'
    : sells
      ? 'Connect your Xero: invoices and credit notes go to it and payments come back'
      : 'Connect your Xero: approved timesheets and leave are sent to Xero Payroll',
});

// Cards taken at the counter go into the nursery's own Stripe account.
const CARD_PAYMENTS: Tile = { label: 'Card payments', icon: CreditCard, path: '/accounts/stripe',
  description: 'Connect your own Stripe account to take cards at the counter' };

const MYOB: Tile = { label: 'MYOB', icon: Plug, path: '/accounts/myob',
  description: 'Connect MYOB AccountRight for payroll and choose the company file' };

const MAPPINGS: Tile = { label: 'Payroll export mappings', icon: ArrowLeftRight, path: '/accounts/payroll-mappings',
  description: 'How each pay category is named in MYOB and Xero' };

function Section({ title, note, tiles }: { title: string; note: string; tiles: Tile[] }) {
  return (
    <section>
      <h2 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">{title}</h2>
      <p className="text-sm text-neutral-500 mt-1 mb-3 break-words">{note}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {tiles.map(({ label, description, icon: Icon, path }) => (
          <Link
            key={path}
            to={path}
            className="flex items-start gap-3 p-4 bg-white border border-neutral-200 rounded-xl hover:border-primary-300 hover:shadow-sm transition-all"
          >
            <Icon className="h-6 w-6 text-primary-600 shrink-0 mt-0.5" />
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-neutral-800 break-words">{label}</span>
              <span className="block text-sm text-neutral-500 break-words">{description}</span>
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function AccountsDashboard() {
  const { hasAccess: isAdmin } = useModuleLicense('accounts', 'admin');
  // Selling is the nursery's; an organisation without it has nothing to invoice.
  const { orgHasModule: sells, loading: nurseryLoading } = useModuleLicense('nursery');
  const { orgHasModule: paysStaff, loading: timesheetLoading } = useModuleLicense('timesheet');

  return (
    <>
      <PageHeader
        title="Accounts"
        subtitle="Customers, prices, invoices and getting paid, with your Xero and MYOB connections"
        icon={<Landmark className="h-6 w-6 text-white" />}
      />
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-8">
        {!isAdmin && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900 break-words">
            You hold the Accounts User seat. You can set up customers and price lists, authorise invoices and
            handle disputes. Credit notes, payments, rebate agreements and the Xero and MYOB connections need
            an Accounts Admin.
          </div>
        )}

        {nurseryLoading || timesheetLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[...Array(6)].map((_, i) => <div key={i} className="h-20 bg-neutral-100 rounded-xl animate-pulse" />)}
          </div>
        ) : (
          <>
            {sells && (
              <Section
                title="Sales and receivables"
                note="The nursery takes orders, picks and despatches. Who is billed, at what price and what they owe is set up here."
                tiles={SALES}
              />
            )}
            {(sells || paysStaff) && (
              <Section
                title="Connections"
                note={paysStaff
                  ? 'The pay run itself is approved and sent from Payroll. Where it and your invoices are sent, and how they are mapped, is set up here.'
                  : 'Where your invoices are sent, and how they are mapped, is set up here.'}
                tiles={[xeroTile(sells, paysStaff), ...(sells ? [CARD_PAYMENTS] : []), ...(paysStaff ? [MYOB, MAPPINGS] : [])]}
              />
            )}
            {!sells && !paysStaff && (
              <div className="bg-white border border-neutral-200 rounded-xl p-6 text-sm text-neutral-600 break-words">
                Accounts works with the Nursery module (customers, invoices and receivables) and with Timesheets
                (the Xero and MYOB payroll connections). Your organisation has neither yet.
              </div>
            )}
          </>
        )}

        <Section
          title="Purchasing"
          note="Orders are raised, sent to the supplier and received by the people your organisation's admin has authorised. Each is numbered for the farm it is for."
          tiles={PURCHASING}
        />
      </div>
    </>
  );
}

export default AccountsDashboard;
